import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useLang } from '@/lib/LanguageContext';
import { useCarnet } from '@/lib/CarnetContext';
import { useLexique } from '@/lib/LexiqueContext';
import { useColophon } from '@/lib/ColophonContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { PoweredByIntralys } from '@/components/shared/PoweredByIntralys';
import { OceanneLogo } from '@/components/ui/oceanne-logo';

// Inline SVG icons (lucide-react v1.16 ships in this project without social brand icons).
// Kept as small monochrome glyphs so they inherit currentColor and stay theme-safe.
function FacebookIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width="16"
      height="16"
      aria-hidden="true"
      {...props}
    >
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12Z" />
    </svg>
  );
}

function LinkedinIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width="16"
      height="16"
      aria-hidden="true"
      {...props}
    >
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05a3.75 3.75 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}

const FB_URL = 'https://www.facebook.com/p/Océanne-Béatrice-Brodeur-61578880271035/';
const LI_URL = 'https://www.linkedin.com/in/océanne-brodeur-69130a240/';

// Phase 27.81 — Pattern dual modal+page (cross-Intralys) :
// - `kind: 'link'` → Link normal (mentions, confidentialité, ressources, pages contenu différent)
// - `kind: 'modal'` → button qui ouvre le modal Carnet/Lexique/Colophon
//   (modal contient CTA "Lire version complète" → page SEO)
type SecondaryNavItem =
  | { key: string; label_fr: string; label_en: string; href: string; kind: 'link' }
  | { key: string; label_fr: string; label_en: string; modal: 'carnet' | 'lexique' | 'colophon'; kind: 'modal' };

const SECONDARY_NAV: ReadonlyArray<SecondaryNavItem> = [
  { key: 'mentions', label_fr: 'Mentions légales', label_en: 'Legal notices', href: '/mentions-legales', kind: 'link' },
  { key: 'privacy', label_fr: 'Confidentialité', label_en: 'Privacy', href: '/confidentialite', kind: 'link' },
  { key: 'lexique', label_fr: 'Lexique', label_en: 'Glossary', modal: 'lexique', kind: 'modal' },
  { key: 'carnet', label_fr: 'Carnet de ressources', label_en: 'Resource book', modal: 'carnet', kind: 'modal' },
  { key: 'colophon', label_fr: 'Colophon', label_en: 'Colophon', modal: 'colophon', kind: 'modal' },
  { key: 'ressources', label_fr: 'Ressources', label_en: 'Resources', href: '/ressources', kind: 'link' },
  { key: 'guide', label_fr: 'Guide gratuit', label_en: 'Free guide', href: '/guide', kind: 'link' }
];

export function SiteFooter() {
  const { lang } = useLang();
  const text = ta(t.footer, lang);
  const carnet = useCarnet();
  const lexique = useLexique();
  const colophon = useColophon();

  const openModal = (key: 'carnet' | 'lexique' | 'colophon') => {
    if (key === 'carnet') carnet.open();
    else if (key === 'lexique') lexique.open();
    else colophon.open();
  };

  return (
    <footer className="w-full border-t border-border/40 bg-background">
      <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-20 md:py-16">
        {/* Main grid */}
        <div className="mb-12 grid grid-cols-1 gap-12 md:grid-cols-3">
          {/* Brand */}
          <ScrollReveal>
            {/* Phase 27.83 — leaf sprig logo sage validé par cliente */}
            <OceanneLogo className="mb-4 h-12 w-auto text-secondary" />
            <p className="mb-3 font-display text-2xl text-foreground md:text-3xl">
              Océanne Brodeur
            </p>
            <p className="font-display text-sm italic text-secondary">
              {lang === 'fr'
                ? 'Conseillère en sécurité financière · Spécialisée AMG'
                : 'Financial Security Advisor · AMG specialist'}
            </p>
          </ScrollReveal>

          {/* Navigation secondaire */}
          <nav aria-label="Pages secondaires">
            <p className="mb-4 font-sans text-xs font-bold uppercase tracking-widest text-foreground/75">
              {lang === 'fr' ? 'Pages' : 'Pages'}
            </p>
            <ul className="space-y-2">
              {SECONDARY_NAV.map((item) => {
                const label = lang === 'fr' ? item.label_fr : item.label_en;
                if (item.kind === 'modal') {
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => openModal(item.modal)}
                        className="font-sans text-sm text-muted-foreground transition-colors hover:text-foreground text-left"
                      >
                        {label}
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      className="font-sans text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Réseaux + AMF */}
          <div>
            <p className="mb-4 font-sans text-xs font-bold uppercase tracking-widest text-foreground/75">
              {lang === 'fr' ? 'Suivre' : 'Follow'}
            </p>
            <div className="mb-6 flex items-center gap-3">
              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground transition-colors hover:bg-secondary/20"
              >
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a
                href={LI_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-foreground transition-colors hover:bg-secondary/20"
              >
                <LinkedinIcon className="h-4 w-4" />
              </a>
            </div>
            <p className="font-sans text-xs italic leading-relaxed text-pretty hyphens-auto text-foreground/80">
              {text.disclaimer_amf}
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col gap-4 border-t border-border/40 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="font-sans text-xs text-foreground/80">{text.copyright}</p>
          <p className="font-sans text-xs italic text-foreground/80">
            {lang === 'fr'
              ? 'Conçu avec soin par Intralys · 2026'
              : 'Crafted with care by Intralys · 2026'}
          </p>
        </div>

        {/* Signature Intralys — pill inline centré sous bottom bar. */}
        <PoweredByIntralys variant="inline" className="mt-8" />
      </div>
    </footer>
  );
}
